import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import axios from "axios";
import Login from "@/components/Login";
import CommandModule from "@/components/CommandModule";
import AdminPanel from "@/components/AdminPanel";
import { Toaster } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ADMIN_TOKEN_KEY = "ism-admin-token";
const isAdminContext = () => window.location.pathname.startsWith("/admin");

export const clearUserSession = () => {
  ["isAuthenticated", "authToken", "refreshToken", "userId", "username"].forEach((k) =>
    localStorage.removeItem(k)
  );
};

// Axios interceptor: attach the right JWT (admin token on /admin, user token elsewhere)
axios.interceptors.request.use((config) => {
  const adminToken = sessionStorage.getItem(ADMIN_TOKEN_KEY);
  const userToken = localStorage.getItem("authToken");
  const token = isAdminContext() && adminToken ? adminToken : userToken || adminToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise = null;
const refreshAccessToken = async () => {
  if (!refreshPromise) {
    const refreshToken = localStorage.getItem("refreshToken");
    refreshPromise = axios
      .post(`${API}/auth/refresh`, { refreshToken })
      .then((res) => {
        if (res.data?.success && res.data?.token) {
          localStorage.setItem("authToken", res.data.token);
          return res.data.token;
        }
        throw new Error("refresh failed");
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

// Handle 401 responses — try one silent refresh, otherwise send to login
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const original = error.config || {};
    const url = original.url || "";

    if (status !== 401 || url.includes("/auth/") || url.includes("/admin/login")) {
      return Promise.reject(error);
    }

    if (isAdminContext()) {
      sessionStorage.removeItem(ADMIN_TOKEN_KEY);
      window.location.href = "/admin";
      return Promise.reject(error);
    }

    if (!original._retried && localStorage.getItem("refreshToken")) {
      original._retried = true;
      try {
        const newToken = await refreshAccessToken();
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newToken}` };
        return axios(original);
      } catch (_) {
        // fall through to logout
      }
    }

    clearUserSession();
    window.location.href = "/";
    return Promise.reject(error);
  }
);

export { API, ADMIN_TOKEN_KEY };

// Protected Route Component — requires a token and pins the URL to the logged-in user
const ProtectedRoute = ({ children }) => {
  const { userId } = useParams();
  const token = localStorage.getItem("authToken");
  const ownUserId = localStorage.getItem("userId");

  if (!token) {
    clearUserSession();
    return <Navigate to="/" replace />;
  }

  if (userId && userId !== "new" && ownUserId && userId !== ownUserId) {
    return <Navigate to={`/dashboard/${ownUserId}`} replace />;
  }

  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Login />} />
          <Route 
            path="/dashboard/:userId/*" 
            element={
              <ProtectedRoute>
                <CommandModule />
              </ProtectedRoute>
            } 
          />
          <Route path="/admin" element={<AdminPanel />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
